/**
 * Vertical card component.
 */
import Card from '@shared/types/props/card';
import ChannelUtils from '../../../../shared/utils/channels';
import Container from '../../atoms/container/verticalCardContainer';
import Image from '../../atoms/images/verticalCardImage';
import Info from './verticalCardInfo';
import MainContext from '../../../../shared/components/utils/context/mainContext';
import useList from '../../../../shared/components/utils/hooks/useList';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useMemo, useState } from 'react';

/**
 * Number of items to display in the vertical rail.
 */
const ITEMS_TO_DISPLAY = 10;

/**
 * Vertical card component. Represents a single vertical card in the vertical rail.
 * Handles lazy loading, focus states, and asset updates on hover.
 *
 * @param {Props} props - Component props including item, index, and onClick handler
 * @returns {JSX.Element | null} Vertical card element or null if not in view
 */

export default function VerticalCard(parentProps: Card) {
  // Card hook
  const { display, props } = useVerticalCard({ ...parentProps });

  // Render
  return (
    <>
      {display.card && (
        <Container id={'verticalCardContainer'} {...props.container}>
          {display.info ? (
            <>
              <Image {...props.image} />
              <Info {...props.info} />
            </>
          ) : null}
        </Container>
      )}
    </>
  );
}

/**
 * Hook for the vertical card component.
 */
function useVerticalCard({ index, item, onClick, releaseAction }: Card) {
  // Selectors
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { hidePreviousItem, setAsset } = useContext(MainContext);
  const { distanceToIndex, focused, listIndex } = useList(index);

  // States
  const [hovered, setHovered] = useState(false);

  // Memoized data
  const { display, props } = useMemo(() => {
    // Fetch upgradable plan
    let upgradablePlan = null;
    if (
      distanceToIndex < ITEMS_TO_DISPLAY &&
      distanceToIndex > -ITEMS_TO_DISPLAY
    ) {
      upgradablePlan = ChannelUtils.getUpgradablePlan(
        item.channel,
        false,
        item.type
      );
    }
    // Return display and props
    return {
      display: {
        card: distanceToIndex < ITEMS_TO_DISPLAY,
        info: distanceToIndex > (hidePreviousItem ? -1 : -ITEMS_TO_DISPLAY),
      },
      props: {
        container: {
          onClick: onClick || releaseAction,
          onMouseEnter: () => {
            setHovered(true);
            setAsset(item);
          },
          onMouseLeave: () => setHovered(false),
        },
        image: {
          src: item?.poster || item?.photo,
        },
        info: {
          item,
          focused: (!enabled && focused && index === listIndex) || hovered,
          plan: upgradablePlan,
        },
      },
    };
  }, [
    index,
    item,
    enabled,
    distanceToIndex,
    focused,
    hidePreviousItem,
    hovered,
    listIndex,
    onClick,
    releaseAction,
    setAsset,
    setHovered,
  ]);

  return { display, props };
}
